import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Sparkles, Database, ShieldAlert, FlaskConical, LogOut, Smartphone, Building2, Target, Menu, X, Plug } from "lucide-react";
import { api, adminLab, setAdminLab, setToken } from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/purpose", label: "Purpose", icon: Target },
  { to: "/admin/personality", label: "Studio", icon: Sparkles },
  { to: "/admin/corpus", label: "Memory", icon: Database },
  { to: "/admin/guardrails", label: "Safety", icon: ShieldAlert },
  { to: "/admin/connections", label: "Connections", icon: Plug },
  { to: "/admin/evaluation", label: "Evaluation", icon: FlaskConical },
  { to: "/admin/labs", label: "Labs", icon: Building2 },
];

const link = ({ isActive }) =>
  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-[#E07A5F]/12 text-[#F4A261]" : "text-[#A1A8B3] hover:bg-white/[0.04] hover:text-[#F3EFE6]"}`;

/* The same furniture the desktop sidebar carries, so the drawer is not a lesser menu. */
const Furniture = ({ active, pickable, onSwitch, onPick, nav }) => (
  <>
    <div className="px-3">
      <p className="eyebrow">Admin Console</p>
      <h1 className="mt-1 font-display text-xl font-semibold">Griot</h1>
      {pickable.length > 1 ? (
        <select data-testid="admin-lab-switcher" value={active?.slug || ""} onChange={onSwitch} className="mt-2 w-full rounded-xl border border-white/10 bg-[#121417] px-2.5 py-2 text-xs text-[#F3EFE6] focus:border-[#E07A5F] focus:outline-none">
          {pickable.map((l) => <option key={l.slug} value={l.slug}>{l.active ? l.name : `${l.name} (closed)`}</option>)}
        </select>
      ) : (
        <p className="mt-1 text-xs text-[#6E7582]" data-testid="admin-lab-name">{active?.name || "…"}</p>
      )}
      <span className="mt-3 inline-flex max-w-full items-center gap-1.5 truncate rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 font-mono text-[10px] text-[#A1A8B3]" style={active ? { borderColor: `${active.accent}55`, color: active.accent } : undefined}>lab_id · {active?.lab_id || "…"}</span>
    </div>
    <nav className="mt-8 flex flex-col gap-1">
      {NAV.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} onClick={onPick} data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`} className={link}>
          <Icon size={16} /> {label}
        </NavLink>
      ))}
    </nav>
    <div className="mt-auto space-y-1 pt-6">
      <button onClick={() => nav("/")} data-testid="admin-open-participant-btn" className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-[#A1A8B3] hover:bg-white/[0.04]"><Smartphone size={16} /> Open Griot</button>
      <button onClick={() => { setToken(""); localStorage.removeItem("griot-admin-lab"); nav("/admin/login", { replace: true }); }} data-testid="admin-logout-btn" className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-[#A1A8B3] hover:bg-white/[0.04]"><LogOut size={16} /> Sign out</button>
    </div>
  </>
);

export default function AdminLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const [labs, setLabs] = useState([]);
  const [open, setOpen] = useState(false);
  const current = adminLab();

  useEffect(() => {
    api.adminLabs().then((rows) => {
      setLabs(rows);
      const openLabs = rows.filter((l) => l.active);
      if (!adminLab() && (openLabs[0] || rows[0])) setAdminLab((openLabs[0] || rows[0]).slug);
    }).catch(() => {});
  }, []);

  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);

  const active = labs.find((l) => l.slug === current) || labs.find((l) => l.active) || labs[0];
  const pickable = labs.filter((l) => l.active || l.slug === active?.slug);
  const onSwitch = (e) => { setAdminLab(e.target.value); window.location.reload(); };
  const here = [...NAV].reverse().find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to)));

  return (
    <div className="flex min-h-screen bg-[#121417] text-[#F3EFE6]" data-testid="admin-layout">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-white/[0.06] bg-[#0F1114] px-4 py-6 md:flex">
        <Furniture active={active} pickable={pickable} onSwitch={onSwitch} nav={nav} />
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="admin-drawer" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" />
          <aside
            className="drawer-in absolute inset-y-0 left-0 flex w-[17.5rem] max-w-[84%] flex-col overflow-y-auto border-r border-white/[0.06] bg-[#0F1114] px-4 py-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button data-testid="admin-drawer-close-btn" onClick={() => setOpen(false)} className="absolute right-3 top-3 rounded-lg p-1.5 text-[#6E7582] hover:bg-white/[0.05] hover:text-[#F3EFE6]"><X size={16} /></button>
            <Furniture active={active} pickable={pickable} onSwitch={onSwitch} onPick={() => setOpen(false)} nav={nav} />
          </aside>
        </div>
      )}

      {/* min-w-0: a flex child defaults to min-width:auto and will grow past the viewport rather
          than shrink, which is what pushed every admin page off the right edge of a phone */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-white/[0.06] bg-[#121417]/95 px-4 py-3 backdrop-blur md:hidden">
          <button data-testid="admin-menu-btn" onClick={() => setOpen(true)} aria-label="Open the menu" className="rounded-xl border border-white/10 p-2 text-[#D8D2C4] active:scale-95">
            <Menu size={17} />
          </button>
          <span className="min-w-0 flex-1 truncate">
            <span className="font-display text-sm font-medium">Griot</span>
            <span className="ml-2 font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#6E7582]">{here?.label || "Admin"}</span>
          </span>
          <span className="shrink-0 truncate font-mono text-[9px] text-[#6E7582]" style={active ? { color: active.accent } : undefined}>{active?.slug || ""}</span>
        </header>
        <main className="min-w-0 flex-1 overflow-x-hidden px-4 py-6 sm:px-6 md:px-10 md:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
